import { afterNextRender, Component, inject, signal } from '@angular/core';
import { AboutUsProducts } from '../../../shared/types/common.types';
import { Router, RouterLink } from '@angular/router';
import { SeoService } from '../../../shared/seo.service';
import { SEO_PAGES_CONFIG } from '../../../shared/header/header.lists';
import { Pages } from '../../../shared/types/common.enums';
import { ModalContentService } from '../../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../../shared/constants/modal-content-types';

@Component({
  selector: 'app-products',
  imports: [
    RouterLink
  ],
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss'
})
export class ProductsComponent {

  //#region Services

  // #sanitizer = inject(DomSanitizer);

  #seoService = inject(SeoService);

  #router = inject(Router);

  #modalContentService = inject(ModalContentService);

  //#endregion

  //#region Properties

  products = signal<AboutUsProducts[]>([]);

  //#endregion

  //#region Constructor

  constructor() {
    this.#seoService.setUpSEOTags(SEO_PAGES_CONFIG[Pages.product]);
    afterNextRender(() => this.#loadProducts());
  }

  //#endregion

  //#region Methods

  #loadProducts() {
    this.products.set([
      {
        img: 'raw-cows-milk.webp',
        title: 'Raw Cows Milk for Pets',
        descriptionArray: [
          'Our Jersey dairy cows roam the pastures of Carlton Farms 365 days per year. In addition to the grass they harvest from the pasture, our cows also eat grass that we have harvested in the form of hay or green-chop (see Facebook videos, for example). Naturally, we ensure that the cows have a consistent supply of fresh grass that has not been sprayed with pesticides or herbicides. We use no hormones or sub-therapeutic antibiotics. Our cows are milked individually in our three-stall milking barn. As each cow voluntarily comes into the barn to be milked, she is first given a scoop of feed. The amount of feed varies based on each particular cow and what her body demands are at that particular time, but the amount is always much smaller than a standard commercial dairy. On average, our cows receive only about 5 lbs of feed daily (commercial dairies often feed 30 lbs per cow). We regard this feeding as a treat that serves to keep the girls calm and happy during the milking time. Before milking, each cow’s udder is washed thoroughly with warm water, sanitized, and dried. Sanitized milkers are then attached to the cow for a process of actual milking that only takes about 5-10 minutes. Our milking system is a former Grade A facility that uses a pipeline system. The advantage of a pipeline system is the rapid cooling of the milk. Our milk is cooled within 2-3 minutes of coming out of the cow. As the milk travels from the milking room to the adjacent “tank” room, it passes through a micro-particle filter that ensures the milk is clean. The milk is then bottled right out of the cooling tank as Raw Cows Milk for Pets. Nothing is ever added to the milk, and nothing is taken away from the milk.'
        ],
        alt: 'Cows and calves grazing in a pasture during sunset with a close-up of a black-and-white cow in the foreground.'
      },
      {
        img: 'eggs.webp',
        title: 'Free Range, Pasture Raised Eggs',
        descriptionArray: [
          'Our free-range hens spend their days picking through the pasture, eating grass and whatever unlucky bug they may run across. The hens may also seek shelter from the heat in our custom-designed chicken barn, which houses their water, food, and nesting boxes. They can go in the barn or out on the pasture as they, please. In addition to their natural diet of grass and bugs, our chickens receive a non-GMO feed that we source locally.'
        ],
        alt: 'Wicker basket filled with a variety of farm-fresh eggs in white, brown, and blue-green shades on a wooden surface.'
      },
      {
        img: 'grass-fed-beef.webp',
        title: 'Grass Fed Beef',
        descriptionArray: [
          'Our beef cattle also roam our pastures 365 days per year. The beef cows are never given hormones or antibiotics and only have a regular diet of grass... no feed. Of course, we put up baled silage "baleage" from rye grass, sudan grass, millet & oat grass to get through the winter months. Baleage is forage that is baled at around 50% moisture (so not dry hay) and wrapped to be in an oxygen-free environment. The lack of oxygen allows the grass to "culture" rather than rot. Similar to all the lacto-fermented foods that we enjoy. The final product is more nutritious and palatable for the cattle. This baleage obviously receives no pesticides or herbicides, allowing us to feed the cattle through the winter. Our slow-growing cattle take over 2 years to reach harvest weight, but they are worth the wait. Our animals are finished entirely on grass with no "grain finishing," as many farms do.'
        ],
        alt: 'Three raw grass-fed beef filet steaks on brown paper with coarse salt, peppercorns, and fresh herbs.'
      },
      {
        img: 'pasture-raised-pork.webp',
        title: 'Pasture Raised Pork',
        descriptionArray: [
          'Even our pigs spend their days roaming the pasture or sometimes lounging in a mud wallow. We provide them with a shelter to seek refuge from the heat, rain, or wind. Pigs will eat some grass and roots and love to seek out acorns from the wooded areas of the pasture. However, like the chickens, we do have to feed the pigs, as their digestive system cannot survive on grass alone. We provide the pigs a non-GMO feed that we source locally.'
        ],
        alt: 'Raw pork cuts on butcher paper garnished with rosemary and surrounded by seasonings.'
      },
      {
        img: 'turkeys.webp',
        title: 'Pasture Raised Turkey',
        descriptionArray: [
          'Free Range Turkeys are available during the Thanksgiving season. These turkeys are raised on pasture and fed a non-GMO diet that we source locally. Turkeys are rotated onto fresh pasture every few days so that they are allowed access to plenty of forage and insects.'
        ],
        alt: 'Group of red hens walking around the entrance of a rustic wooden coop.'
      },
      {
        img: 'chicken.webp',
        title: 'Pasture Raised Chicken',
        descriptionArray: [
          'Our pasture-raised slow-growing meat chickens have the pleasure of roaming on acres of pasture, free to enjoy the sunlight and fresh air as well as a smorgasbord of worms, insects, bugs & grass. They also have access to non-GMO feed, which is sourced locally. If they want shelter, they can get out of the elements in their custom-designed chicken barn. There they can nest, rest and drink fresh water. The chickens are moved every few days to ensure the freshest environment possible. Our pastures never receive pesticides or herbicides of any kind, providing the best-growing conditions for meat chickens bearing the Carlton Farm name.'
        ],
        alt: 'Group of red hens walking around the entrance of a rustic wooden coop.'
      },
      {
        img: 'stewing-hen.webp',
        title: 'Pasture Raised Stewing Hens',
        descriptionArray: [
          'We like to keep our egg-laying hens for two years so that they can live a full life. They are allowed to molt naturally, and we never withhold feed or use lighting to force molting. However, eventually, their egg-producing days are behind them. When that time comes, we process those birds and make them available for stewing hens. The meat is unsuitable for roasting, but these birds make excellent chicken soup/stock, and their age adds richness to the flavor of the stock.'
        ],
        alt: 'Group of red hens walking around the entrance of a rustic wooden coop.'
      },
      {
        img: 'grass-fed-lamb.webp',
        title: 'Grass Fed Lamb (Seasonal)',
        descriptionArray: [
          'Our lamb is raised on pasture and fed exclusively mothers milk (for the first few months) and grass. The only difference between our lamb and our other animals is that it is only available seasonally. Lambs are harvested in the spring & fall.'
        ],
        alt: 'Close-up of a curious sheep looking at the camera with other sheep grazing in the background.'
      },
      {
        img: 'organic-vegetable.webp',
        title: 'Fruits & Vegetables (Seasonal)',
        descriptionArray: [
          'The animals keep us busy, so we grow a limited selection of vegetables here at Carlton Farms. However, we work with several friends who grow organic vegetables and fruits, which are available seasonally.',
          'Our Farmer friends that we work with to supply many a plethora of vegetables and fruits include Daniel - Delano Community Farm, Brian Heatherington - Beech Creek Farm, and Wes Swancy - Riverview Farm, to name a few.',
          'Working with other farms to provide vegetables ensures a more diverse availability than if we were only harvesting out of one field. There is a sense of community in the local natural farming world, and we like to embrace that community. We try to be the best at what we produce, and we are proud to work with other farmers who produce things that we do not while using the same level of integrity.'
        ],
        alt: 'Hand holding three freshly harvested, soil-covered carrots with green tops.'
      },
      {
        img: 'other-products.webp',
        title: 'Other Products',
        alt: 'Large red barn with a white silo in a wide-open field under a cloudy sky.',
        isOtherProducts: true
      },
    ])
  }

  // #sanitizeHtml(html: string): SafeHtml {
  //   const parser = new DOMParser();
  //   const doc = parser.parseFromString(html, 'text/html');
  //   return this.#sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  // }

  readMore(item: AboutUsProducts) {

    this.#modalContentService
      .openModal(ModalContentTypes.CONFIRMATION, {
        title: item.title,
        textContent: item.descriptionArray?.map(x => `<p style="text-align: justify;">${x}</p>`).join(''),
        confirmButtonText: 'SHOP NOW'
      })
      .closed
      .subscribe(value => {
        if (value.confirm)
          this.#router.navigate(['/shop']);
      });
  }

  //#endregion
}
